import { type Grade, type RankKey, type RunResult, compareRank, gradeOf } from '../engine';
import { t } from '../i18n';
import { ko } from '../i18n/ko';

/**
 * 결과 카드 — 텍스트 4줄 (UPLIFT §8 · ADR-023 · ADR-028). 카드는 하나다: 도전 진입 화면·HUD 목표·결과 비교표가
 * 전부 이 빌더의 입력값을 읽는다.
 *
 * 스포일러 절대 조항(설계원칙 3): CardInput 에 questionId·answer·tiles·Question 이 **없다.** 8칸 스트립은 문제의 성질이
 * 아니라 발신자의 성적이고, 정답 길이 프로파일은 전 판 공통이라 새로 새는 정보가 없다.
 */
export type StripMark = '🟧' | '🟨' | '🟥' | '⬜';

export interface CardRecord {
  readonly outcome: 'solved' | 'skipped' | 'died';
  readonly wrongEats: number;
}

export interface CardInput {
  readonly records: readonly CardRecord[];
  readonly runWordCount: number;
  readonly sealed: boolean;
  readonly solved: number;
  readonly sealMs: number; // 봉인 성공 시 마지막 정답까지의 명목 ms, 아니면 -1
  readonly grade: Grade;
  readonly reviveUsed: number;
  readonly gameIndex: number; // 이 세션 N번째 판 (1부터)
  readonly hiddenMs: number; // 백그라운드에서 답을 찾고 온 판은 시간을 자랑하지 않는다
  readonly personalBest: boolean;
  readonly duel: DuelOutcome | null;
  readonly url: string; // '' 이면 4행 생략
}

export type DuelOutcome = 'win' | 'lose' | 'draw';

/** 등급 라벨(한국어) — 문안은 사람이 정한다(docs/04 미결). 카드는 현재 로케일의 `t().card.grade` 를 쓴다. */
export const GRADE_LABEL: Readonly<Record<Grade, string>> = ko.card.grade;

/** 등급을 표시하려면 최소 3문제에 도달해야 한다 — 8초 만에 죽은 판을 등급화하지 않는다. */
export const GRADE_MIN_REACHED = 3;

export const CARD_SOLVED_PREDICATE = (r: CardRecord): boolean => r.outcome === 'solved';

export function stripOf(records: readonly CardRecord[], total: number): StripMark[] {
  const out: StripMark[] = [];
  for (let i = 0; i < total; i++) {
    const r = records[i];
    if (r === undefined) out.push('⬜');
    else if (!CARD_SOLVED_PREDICATE(r)) out.push('🟥');
    else out.push(r.wrongEats === 0 ? '🟧' : '🟨');
  }
  return out;
}

export function formatClockMs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function cardLines(c: CardInput): string[] {
  const reached = c.records.length;
  const S = t().card;
  const head = reached >= GRADE_MIN_REACHED ? S.head(S.grade[c.grade], c.grade) : S.headFirst;
  const strip = stripOf(c.records, c.runWordCount).join('');
  const line2 = `${strip} ${c.solved}/${c.runWordCount}`;
  const tokens: string[] = [];
  if (c.sealed)
    tokens.push(
      c.hiddenMs === 0 && c.sealMs >= 0 ? S.sealed(formatClockMs(c.sealMs)) : S.sealedNoTime,
    );
  else tokens.push(S.solved(c.solved, c.runWordCount));
  if (c.reviveUsed > 0) tokens.push(S.revive(c.reviveUsed));
  if (c.gameIndex >= 2) tokens.push(S.nth(c.gameIndex));
  if (c.personalBest) tokens.push(S.best);
  if (c.duel === 'win') tokens.push(S.win);
  else if (c.duel === 'lose') tokens.push(S.lose);
  else if (c.duel === 'draw') tokens.push(S.draw);
  const line3 = tokens.join(' · ');
  const lines = [head, line2, line3];
  if (c.url !== '') lines.push(S.url(c.url));
  return lines;
}

export function cardText(c: CardInput): string {
  return cardLines(c).join('\n');
}

/** RunResult → 카드 입력. 정답·문제 id 는 여기서 떨어져 나간다. */
export function cardInputOf(
  r: RunResult,
  extra: {
    readonly runWordCount: number;
    readonly nominalTickMs: number;
    readonly gameIndex: number;
    readonly hiddenMs: number;
    readonly personalBest: boolean;
    readonly duel: DuelOutcome | null;
    readonly url: string;
  },
): CardInput {
  return {
    records: r.records.map((w) => ({ outcome: w.outcome, wrongEats: w.wrongEats })),
    runWordCount: extra.runWordCount,
    sealed: r.sealed,
    solved: r.solved,
    sealMs: r.sealed && r.sealTicks >= 0 ? r.sealTicks * extra.nominalTickMs : -1,
    grade: gradeOf(r),
    reviveUsed: r.reviveUsed,
    gameIndex: extra.gameIndex,
    hiddenMs: extra.hiddenMs,
    personalBest: extra.personalBest,
    duel: extra.duel,
    url: extra.url,
  };
}

/** 대결 승패 — 순위 키 하나로(ADR-023 · ADR-028). 동점은 무승부. */
export function duelOutcome(mine: RankKey, theirs: RankKey): DuelOutcome {
  const c = compareRank(mine, theirs);
  return c < 0 ? 'win' : c > 0 ? 'lose' : 'draw';
}

/** 상대 기록 한 줄 — 도전 진입 화면·HUD 목표가 같은 문장을 쓴다. */
export function describeOpponent(
  rec: { readonly sealed: boolean; readonly solved: number; readonly lastSolveTick: number },
  runWordCount: number,
  nominalTickMs: number,
): string {
  const S = t().opponent;
  if (rec.sealed) {
    return rec.lastSolveTick >= 0
      ? S.sealed(formatClockMs(rec.lastSolveTick * nominalTickMs))
      : S.sealedNoTime;
  }
  return S.solved(rec.solved, runWordCount);
}
